import { useCallback, useContext, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useLocation } from "react-router-dom";

import { ArrowheadRightDarkIcon, CompleteBlueIcon } from "../../../../assets";
import { UserType } from "../../../../constants";
import { ProjectPhase } from "../../../../constants/project-phase";
import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, ResultType, TaskPanelData } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { GetTaskBoardResponse, searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../service/query";
import { getTaskBoard } from "../../../../service/query/QueryService.full";
import { ResultData, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { flattenObject } from "../../../../utils";
import { getProjectDetailsByUuid, getWorkflowTasksBoardRoute } from "../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridCursorDataLoadEventHandler,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
} from "../../../../widget";
import { AvatarCellFormatter } from "../../../../widget/data/DataGrid/components/CellFormatters";
import { IconCellFormatterData, IconLinkCellFormatterData } from "../../../../widget/data/DataGrid/models";
import { LandholdingContext } from "../LandholdingContext";

interface UseTasksReturnData {
  defaultSortingCriteria: SortColumn[];
  isTasksLoading: boolean;
  tasksOnChange: DataGridCursorDataLoadEventHandler;

  workflow: GetTaskBoardResponse["results"][number] | undefined;

  selectedCompleteTask: TaskPanelData | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;

  showCompleteTaskModal: boolean;
  handleCloseCompleteTaskModal: () => void;

  showWorkflowTaskPanel: boolean;
  handleCloseWorkflowTaskPanel: () => void;

  handleRefresh: () => void;
  refreshTasks: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[];
  currentUserType: UserType;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
  {
    name: "Task",
    key: "taskLink",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    filterable: true,
    minWidth: 340,
    sortable: true,
  },
  {
    key: "projectLink",
    name: "Project",
    dataType: "string",
    formatter: "link",
    alignment: "left",
    filterable: true,
    minWidth: 140,
    sortable: true,
  },
  {
    name: "Assigned to",
    key: "avatar",
    dataType: "string",
    formatter: "custom",
    alignment: "center",
    customCellFormatter: AvatarCellFormatter,
    filterable: true,
    minWidth: 140,
    sortable: true,
  },
  {
    key: "status",
    name: "Status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
    minWidth: 140,
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
    minWidth: 140,
  },
  {
    key: "updatedAt",
    name: "Date updated",
    dataType: "Date",
    formatter: "dateOnly",
    filterable: false,
    minWidth: 140,
  },
  {
    name: "Complete",
    key: "completeIcon",
    dataType: "boolean",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 100,
  },
  {
    key: "viewTasksBoardIcon",
    name: "Tasks board",
    dataType: "string",
    formatter: "iconLink",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 100,
  },
];

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];

export const useTasks = (): UseTasksReturnData => {
  const location = useLocation();

  const { landholdingDetails } = useContext(LandholdingContext);
  const { currentUserType } = useAuth();

  const [workflow, setWorkflow] = useState<GetTaskBoardResponse["results"][number]>();

  const [selectedCompleteTask, setSelectedCompleteTask] = useState<TaskPanelData>();
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();

  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);

  const [isTasksLoading, setIsTasksLoading] = useState(true);

  const [refreshTasks, setRefreshTasks] = useState(false);

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const handleCloseCompleteTaskModal = (): void => {
    setSelectedCompleteTask(undefined);
    setShowCompleteTaskModal(false);
  };

  // --- For upcoming tasks data grid ---
  const formatTasksData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };
        result.avatar = { fullName: d.assignee?.name };
        result.taskLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        if (d.objectUuid) {
          result.projectLink = {
            text: d.workflowObjectName,
            to: getProjectDetailsByUuid(d.objectUuid, currentUserType),
            state: { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin },
          } as DataGridLinkCellFormatterData;
        }

        result.viewTasksBoardIcon = {
          to: getWorkflowTasksBoardRoute(d.workflowUuid, currentUserType),
          state: { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin },
          icon: <ArrowheadRightDarkIcon />,
        } as IconLinkCellFormatterData;

        if (d.status !== TaskConstants.TASK_STATUS_UNASSIGNED)
          result.completeIcon = {
            action: () => {
              setSelectedCompleteTask({
                deadlineDate: d.deadlineDate,
                isMandatory: d.isMandatory,
                isMilestone: d.isMilestone,
                keyDate: d.keyDate,
                status: d.status,
                repeat: d.repeat,
                description: d.description,
                name: d.name,
                visibility: [],
                assignedToUserUuid: d.assignee?.uuid ?? null,
                assignedToUserName: d.assignee?.name ?? null,
                ownerOrganisationUuid: d.ownerOrganisation.uuid,
                relateToActivity: d.objectType === "Activity",
                activityUuid: d.objectType === "Activity" ? d.objectUuid : null,
                stepUuid: d.step.uuid,
                phase: d.step.phase as ProjectPhase,
                uuid: d.uuid,
                rowVersion: d.rowVersion,
                resolution: d.resolution,
              });
              setShowCompleteTaskModal(true);
            },
            icon: <CompleteBlueIcon width={20} height={20} strokeWidth={1.5} />,
          } as IconCellFormatterData;

        return result;
      }) || []
    );
  }, []);

  const tasksOnChange: DataGridCursorDataLoadEventHandler = async ({
    paging,
    filtering,
    sorting,
  }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    setIsTasksLoading(true);

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    filterCriteria.groups = {
      some: {
        groupUuid: {
          operator: "eq",
          value: landholdingDetails?.groupGuid,
        },
      },
    };

    if (filterCriteria.taskLink) {
      filterCriteria.name = filterCriteria.taskLink;
      filterCriteria.taskLink = undefined;
      delete filterCriteria.taskLink;
    }

    if (filterCriteria.projectLink) {
      filterCriteria.projectName = filterCriteria.projectLink;
      filterCriteria.projectLink = undefined;
      delete filterCriteria.projectLink;
    }

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }

    const sortKeyMap: Record<string, string> = {
      projectLink: "projectName",
      taskLink: "name",
      avatar: "assignee.name",
    };

    await searchWorkflowTasks({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((s: { key: any; direction: any }) => ({
        key: sortKeyMap[s.key] || s.key,
        direction: s.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatTasksData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setIsTasksLoading(false);
      });
    return data;
  };

  const getAndSetWorkflow = useCallback(async (): Promise<void> => {
    const res = await getTaskBoard({
      showComplete: true,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 1,
      },
      filter: {
        results: {
          uuid: {
            operator: "eq",
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            value: selectedPanelTask!.workflowUuid,
          },
        },
      },
    });
    if (res.status === Status.Success && res.data) {
      setWorkflow(res.data.results[0]);
    }
  }, [selectedPanelTask?.workflowUuid]);

  useEffect(() => {
    if (selectedPanelTask?.workflowUuid) {
      getAndSetWorkflow();
    }
  }, [selectedPanelTask?.workflowUuid]);

  return {
    columns,
    refreshTasks,
    handleRefresh,

    selectedCompleteTask,
    showCompleteTaskModal,
    handleCloseCompleteTaskModal,

    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,

    workflow,
    isTasksLoading,
    tasksOnChange,
    defaultSortingCriteria,
    currentUserType,
  };
};
